import type { InputSize } from "@brexhq/component-library";
import {
  OptionInput,
  Search as SearchIcon,
  Spinner,
} from "@brexhq/component-library";
import styled from "@emotion/styled";
import * as React from "react";

import { useIndustrySearchResultsQuery } from "../../hooks/useIndustrySearchResultsQuery";

export interface SearchResult<V> {
  label: string;
  value: V;
}

const NoSearchResultsContainer = styled.div(({ theme }) => ({
  color: theme.colors.content.secondary,
  padding: 30,
}));

export const NoSearchResults = () => (
  <NoSearchResultsContainer>
    <div>No results found. Please try adjusting your search.</div>
  </NoSearchResultsContainer>
);

export interface IndustrySelectProps {
  onValueChange: (
    newValue: { value: string; label: string } | undefined,
  ) => void;
  initialState: SearchResult<string>;
  label?: string;
  size?: InputSize;
  error?: string;
}

export const IndustrySelect: React.FC<IndustrySelectProps> = ({
  initialState,
  onValueChange,
  label,
  size,
  error,
}) => {
  /**
   * The currently selected SearchResult
   */
  const [selectedItem, setSelectedItem] =
    React.useState<SearchResult<string>>(initialState);
  React.useEffect(() => {
    onValueChange(selectedItem); // emit the selected value whenever it changes
  }, [onValueChange, selectedItem]);

  /**
   * The current search results to display. undefined if there are no search results to display
   */
  const { debouncedSearch, industrySearchResults, loading, clearSearch } =
    useIndustrySearchResultsQuery();

  const onChange = React.useCallback(
    (newValue: { value: string }) => {
      // update the selected item and display text
      setSelectedItem({
        label:
          industrySearchResults?.find(
            (result) => result.value === newValue.value,
          )?.label || newValue.value,
        value: newValue.value,
      });
      clearSearch();
      if (newValue?.value) {
        // trackEvent(
        //   OnboardingAnalyticsEvents.OnboardingSegmentationIndustrySearchSelected,
        //   { industryName: newValue.value },
        // );
      }
    },
    [setSelectedItem, industrySearchResults, clearSearch],
  );

  const noSearchResults: boolean = !loading && !industrySearchResults?.length;

  return (
    <>
      <OptionInput
        name="industry"
        placeholder=""
        label={label}
        value={selectedItem?.value}
        beforeInput={loading ? <Spinner /> : <SearchIcon />}
        afterInput={<span />}
        enableFiltering
        options={industrySearchResults}
        onFilterChange={debouncedSearch}
        onChange={onChange}
        optionsFilter={(options) => options}
        onBlur={clearSearch} // clear search term on blur, so the full list is displayed next time the user opens it
        size={size}
        error={error}
      />
      {noSearchResults ? <NoSearchResults /> : null}
    </>
  );
};
